class EntityExampleComponent {
  constructor(elem, selectKey) {
    this.elem = elem;
    this.formatSelect = this.elem.querySelector(".select-format");
    this.targetSelect = this.elem.querySelector(".select-target");
    this.targetPanels = this.elem.querySelectorAll(
      ".entity-example-target-panel"
    );
    this.followsTargetSelection = this.elem.hasAttribute("data-follow-target");
    this.formatSelectKey = "entity-example-select-format";
    this.targetSelectKey = "entity-example-select-target";

    this.suppressBroadcast = false;

    this.addEventListeners();
    this.initializeSelects();

    document.addEventListener(
      "formatSelected",
      this.onFormatSelected.bind(this)
    );
    document.addEventListener(
      "targetSelected",
      this.onTargetSelected.bind(this)
    );
  }

  initializeSelects() {
    if (this.targetSelect) {
      try {
        if (localStorage.getItem(this.targetSelectKey) !== null) {
          const storedOption = localStorage.getItem(this.targetSelectKey);
          if (
            storedOption &&
            [...this.targetSelect.options].some(
              (opt) => opt.value === storedOption
            )
          ) {
            this.targetSelect.value = storedOption;
          }
        }
      } catch (error) {
        console.log(error);
      }
      this.suppressBroadcast = true;
      this.targetSelect.dispatchEvent(new Event("change"));
      this.suppressBroadcast = false;
    } else if (this.followsTargetSelection && this.pageHasTargetSelection()) {
      this.applyTarget(this.storedTargetSelection(), {
        fallbackToFirstPanel: true,
      });
    } else {
      const targetPanel = this.elem.querySelector(
        ".entity-example-target-panel"
      );
      if (targetPanel) {
        targetPanel.classList.remove("hidden");
      }
      this.elem
        .querySelector(".entity-example-format-panel")
        .classList.remove("hidden");
    }
    if (this.formatSelect) {
      try {
        if (localStorage.getItem(this.formatSelectKey) !== null) {
          const storedOption = localStorage.getItem(this.formatSelectKey);
          if (
            storedOption &&
            [...this.formatSelect.options].some(
              (opt) => opt.value === storedOption
            )
          ) {
            this.formatSelect.value = storedOption;
          }
        }
      } catch (error) {
        console.log(error);
      }
      this.suppressBroadcast = true;
      this.formatSelect.dispatchEvent(new Event("change"));
      this.suppressBroadcast = false;
    }
  }

  addEventListeners() {
    if (this.targetSelect) {
      this.targetSelect.addEventListener(
        "change",
        this.onTargetChange.bind(this)
      );
    }
    if (this.formatSelect) {
      this.formatSelect.addEventListener(
        "change",
        this.onFormatChange.bind(this)
      );
    }
  }

  onFormatChange(event) {
    event.stopPropagation();
    const select = event.currentTarget;

    this.elem
      .querySelectorAll(".entity-example-format-panel")
      .forEach((panel) => {
        if (panel.dataset.format === select.value) {
          panel.classList.remove("hidden");
        } else {
          panel.classList.add("hidden");
        }
      });
    localStorage.setItem(this.formatSelectKey, select.value);

    if (!this.suppressBroadcast) {
      document.dispatchEvent(
        new CustomEvent("formatSelected", { detail: { option: select.value } })
      );
    }
  }

  onTargetChange(event) {
    event.stopPropagation();
    const select = event.currentTarget;

    this.elem
      .querySelectorAll(".entity-example-target-panel")
      .forEach((panel) => {
        if (panel.dataset.target === select.value) {
          panel.classList.remove("hidden");
        } else {
          panel.classList.add("hidden");
        }
      });
    localStorage.setItem(this.targetSelectKey, select.value);

    if (!this.suppressBroadcast) {
      document.dispatchEvent(
        new CustomEvent("targetSelected", { detail: { option: select.value } })
      );
    }
  }

  onFormatSelected(event) {
    const { option } = event.detail;
    if (this.formatSelect) {
      const optionElement = this.findOptionForSelect(option, this.formatSelect);

      if (optionElement) {
        this.formatSelect.value = optionElement.value;
        this.suppressBroadcast = true;
        this.formatSelect.dispatchEvent(new Event("change", { bubbles: false }));
        this.suppressBroadcast = false;
      }
    }
  }

  onTargetSelected(event) {
    const { option } = event.detail;
    if (this.targetSelect) {
      const optionElement = this.findOptionForSelect(option, this.targetSelect);

      if (optionElement) {
        this.targetSelect.value = optionElement.value;
        this.suppressBroadcast = true;
        this.targetSelect.dispatchEvent(new Event("change", { bubbles: false }));
        this.suppressBroadcast = false;
      }
    } else if (this.followsTargetSelection && this.pageHasTargetSelection()) {
      this.applyTarget(option);
    }
  }

  // A follower mirrors the selection a target selector elsewhere on the page
  // owns. A page without one has no selection to mirror, so a stale stored
  // value or URL parameter can't hide the block with no way back.
  pageHasTargetSelection() {
    return document.querySelector(".select-target") !== null;
  }

  // A target-selecting block earlier in the DOM initializes first and writes
  // this key with the value it settles on, so a follower block reading it at
  // init never sees a value this page doesn't offer.
  storedTargetSelection() {
    try {
      return localStorage.getItem(this.targetSelectKey);
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  applyTarget(target, { fallbackToFirstPanel = false } = {}) {
    const matchingPanels = Array.from(this.targetPanels).filter(
      (panel) => panel.dataset.target === target
    );

    if (matchingPanels.length > 0) {
      this.elem.classList.remove("hidden");
      this.targetPanels.forEach((panel) => {
        panel.classList.toggle("hidden", !matchingPanels.includes(panel));
      });
    } else if (target === null && fallbackToFirstPanel) {
      this.targetPanels[0].classList.remove("hidden");
    } else {
      this.elem.classList.add("hidden");
    }
  }

  findOptionForSelect(option, selectElement) {
    return Array.from(selectElement.options).find((o) => o.value === option);
  }
}

export default class EntityExample {
  constructor() {
    document.querySelectorAll(".entity-example").forEach((elem) => {
      new EntityExampleComponent(elem);
    });

    // Check if a dropdown query param exists in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const formatOption = urlParams.get("format");
    if (formatOption) {
      this.selectDropdownOption(formatOption, "formatSelected");
    }

    const targetOption = urlParams.get("target");
    if (targetOption) {
      this.selectDropdownOption(targetOption, "targetSelected");
    }
  }

  selectDropdownOption(option, eventName) {
    const event = new CustomEvent(eventName, { detail: { option } });
    document.dispatchEvent(event);
  }
}
